import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  BarChart, ResponsiveContainer, XAxis, YAxis, Bar, Tooltip, 
  LineChart, Line, PieChart, Pie, Cell, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ComposedChart, Area, AreaChart, ScatterChart, Scatter
} from 'recharts';
import useStore from '@/store/useStore';
import { TrendingUp, TrendingDown, Users, Trophy, Target, Activity, BarChart3, GitCompare } from 'lucide-react';
import TournamentOverview from '@/components/statistics/TournamentOverview';
import TournamentSelector from '@/components/statistics/TournamentSelector';
import TournamentComparison from '@/components/statistics/TournamentComparison';
import { MultiSelectCombobox } from '@/components/ui/MultiSelectCombobox';

const COLORS = ['#0A5F38', '#3498DB', '#E74C3C', '#F39C12', '#9B59B6', '#1ABC9C', '#34495E', '#E67E22'];

const StatisticsPage = () => {
  const { teams, matches, tournaments, players } = useStore();
  const [viewMode, setViewMode] = useState<'overview' | 'comparison'>('overview');
  const [selectedTournaments, setSelectedTournaments] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('tournament-overview');
  const [selectedTournamentsForComparison, setSelectedTournamentsForComparison] = useState<string[]>([]);

  // Calculate comprehensive statistics
  const stats = useMemo(() => {
    const tournamentStats = tournaments.map(tournament => {
      const tournamentMatches = matches.filter(m => m.tournamentId === tournament.id);
      const tournamentTeams = teams.filter(t => t.tournamentId === tournament.id);
      
      const totalRuns = tournamentMatches.reduce((sum, match) => 
        sum + match.team1Score.runs + match.team2Score.runs, 0);
      const totalWickets = tournamentMatches.reduce((sum, match) => 
        sum + match.team1Score.wickets + match.team2Score.wickets, 0);
      const avgRunsPerMatch = tournamentMatches.length ? (totalRuns / tournamentMatches.length) : 0;
      const avgWicketsPerMatch = tournamentMatches.length ? (totalWickets / tournamentMatches.length) : 0;

      return {
        id: tournament.id,
        name: tournament.name,
        location: tournament.location,
        matches: tournamentMatches.length,
        teams: tournamentTeams.length,
        totalRuns,
        totalWickets,
        avgRunsPerMatch: Number(avgRunsPerMatch.toFixed(1)),
        avgWicketsPerMatch: Number(avgWicketsPerMatch.toFixed(1)),
        completedMatches: tournamentMatches.filter(m => m.status === 'completed').length,
        liveMatches: tournamentMatches.filter(m => m.status === 'live').length,
        upcomingMatches: tournamentMatches.filter(m => m.status === 'upcoming').length
      };
    });

    const teamStats = teams.map(team => {
      const teamMatches = matches.filter(m => m.team1Id === team.id || m.team2Id === team.id);
      const teamPlayers = players.filter(p => p.teamIds && p.teamIds.includes(team.id));
      
      let totalRuns = 0;
      let totalWickets = 0;
      let wins = 0;
      
      teamMatches.forEach(match => {
        if (match.team1Id === team.id) {
          totalRuns += match.team1Score.runs;
          totalWickets += match.team1Score.wickets;
          if (match.status === 'completed' && match.team1Score.runs > match.team2Score.runs) wins++;
        } else {
          totalRuns += match.team2Score.runs;
          totalWickets += match.team2Score.wickets;
          if (match.status === 'completed' && match.team2Score.runs > match.team1Score.runs) wins++;
        }
      });

      const completedMatches = teamMatches.filter(m => m.status === 'completed').length;
      const winRate = completedMatches ? ((wins / completedMatches) * 100) : 0;
      const avgRuns = teamMatches.length ? (totalRuns / teamMatches.length) : 0;

      return {
        id: team.id,
        name: team.name,
        shortCode: team.shortCode,
        matches: teamMatches.length,
        players: teamPlayers.length,
        wins,
        totalRuns,
        totalWickets,
        winRate: Number(winRate.toFixed(1)),
        avgRuns: Number(avgRuns.toFixed(1)),
        batsmen: teamPlayers.filter(p => p.role === 'batsman').length,
        bowlers: teamPlayers.filter(p => p.role === 'bowler').length,
        allRounders: teamPlayers.filter(p => p.role === 'all-rounder').length,
        wicketKeepers: teamPlayers.filter(p => p.role === 'wicket-keeper').length,
        consistency: Math.floor(Math.random() * 100),
        powerplay: Math.floor(Math.random() * 200),
        middle: Math.floor(Math.random() * 300),
        death: Math.floor(Math.random() * 150)
      };
    });

    const playerStats = players.map(player => {
      const playerTeam = teams.find(t => player.teamIds && player.teamIds.includes(t.id));
      const teamMatches = matches.filter(m => player.teamIds && (player.teamIds.includes(m.team1Id) || player.teamIds.includes(m.team2Id)));
      
      return {
        id: player.id,
        name: player.name,
        role: player.role,
        team: playerTeam?.name || 'No Team',
        matches: teamMatches.length,
        runs: Math.floor(Math.random() * 800) + 50,
        wickets: Math.floor(Math.random() * 30),
        average: Number((Math.random() * 40 + 20).toFixed(1)),
        strikeRate: Number((Math.random() * 50 + 100).toFixed(1)),
        economy: Number((Math.random() * 3 + 6).toFixed(1)),
        batting: Math.floor(Math.random() * 100),
        bowling: Math.floor(Math.random() * 100),
        fielding: Math.floor(Math.random() * 100),
        fitness: Math.floor(Math.random() * 100),
        leadership: Math.floor(Math.random() * 100),
        consistency: Math.floor(Math.random() * 100)
      };
    });

    return { tournamentStats, teamStats, playerStats };
  }, [teams, matches, tournaments, players]);

  const getComparisonData = (type: 'tournaments' | 'teams' | 'players') => {
    if (type === 'tournaments') {
      return selectedTournaments.length 
        ? stats.tournamentStats.filter(t => selectedTournaments.includes(t.id))
        : stats.tournamentStats.slice(0, 3);
    } else if (type === 'teams') {
      return selectedTeams.length 
        ? stats.teamStats.filter(t => selectedTeams.includes(t.id))
        : stats.teamStats.slice(0, 3);
    } else {
      return selectedPlayers.length 
        ? stats.playerStats.filter(p => selectedPlayers.includes(p.id))
        : stats.playerStats.slice(0, 5);
    }
  };

  const performanceDistribution = useMemo(() => {
    const ranges = ['0-20', '21-40', '41-60', '61-80', '81-100'];
    return ranges.map(range => ({
      range,
      teams: stats.teamStats.filter(team => {
        const [min, max] = range.split('-').map(Number);
        return team.winRate >= min && team.winRate <= max;
      }).length
    }));
  }, [stats.teamStats]);

  const roleDistribution = useMemo(() => {
    const roles = ['batsman', 'bowler', 'all-rounder', 'wicket-keeper'];
    return roles.map(role => ({
      role: role.replace('-', ' ').toUpperCase(),
      count: players.filter(p => p.role === role).length,
      percentage: Number(((players.filter(p => p.role === role).length / players.length) * 100).toFixed(1))
    }));
  }, [players]);

  const dismissalTypes = [
    { type: 'Bowled', count: 45, percentage: 30 },
    { type: 'Caught', count: 60, percentage: 40 },
    { type: 'LBW', count: 30, percentage: 20 },
    { type: 'Run Out', count: 15, percentage: 10 }
  ];

  const handleMultiSelect = (type: 'tournaments' | 'teams' | 'players', itemId: string, checked: boolean) => {
    if (type === 'tournaments') {
      setSelectedTournaments(prev => 
        checked ? [...prev, itemId] : prev.filter(id => id !== itemId)
      );
    } else if (type === 'teams') {
      setSelectedTeams(prev => 
        checked ? [...prev, itemId] : prev.filter(id => id !== itemId)
      );
    } else if (type === 'players') {
      setSelectedPlayers(prev => 
        checked ? [...prev, itemId] : prev.filter(id => id !== itemId)
      );
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Cricket Analytics</h1>
          <p className="text-gray-500">Comprehensive performance analysis and insights</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xl font-semibold text-foreground">Total Tournaments</CardTitle>
            <Trophy className="h-8 w-8 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-5xl font-bold text-foreground">{tournaments.length}</div>
            <div className="text-base text-muted-foreground">
              {stats.tournamentStats.reduce((sum, t) => sum + t.matches, 0)} total matches
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xl font-semibold text-foreground">Active Teams</CardTitle>
            <Users className="h-8 w-8 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-5xl font-bold text-foreground">{teams.length}</div>
            <div className="text-base text-muted-foreground">
              {players.length} total players
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xl font-semibold text-foreground">Avg Runs/Match</CardTitle>
            <Target className="h-8 w-8 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-5xl font-bold text-foreground">
              {(matches.reduce((sum, match) => sum + match.team1Score.runs + match.team2Score.runs, 0) / Math.max(matches.length, 1)).toFixed(1)}
            </div>
            <div className="text-base text-green-500 flex items-center">
              <TrendingUp className="h-5 w-5 mr-1" />
              High scoring matches
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xl font-semibold text-foreground">Match Activity</CardTitle>
            <Activity className="h-8 w-8 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-5xl font-bold text-foreground">
              {matches.filter(m => m.status === 'live').length}
            </div>
            <div className="text-base text-purple-500">
              Live matches ongoing
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="tournament-overview" className="space-y-6" onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="tournament-overview" className="text-center">Tournaments</TabsTrigger>
          <TabsTrigger value="teams" className="text-center">Teams</TabsTrigger>
          <TabsTrigger value="players" className="text-center">Players</TabsTrigger>
          <TabsTrigger value="matches" className="text-center">Matches</TabsTrigger>
        </TabsList>

        {/* NEW TOURNAMENT OVERVIEW TAB */}
        <TabsContent value="tournament-overview" className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              {viewMode === 'overview' ? 'Tournament Analytics Dashboard' : 'Compare Tournaments'}
            </h2>

            <div className="flex gap-2 items-center">
              {viewMode === 'overview' && (
            <TournamentSelector
              tournaments={tournaments}
              selectedTournamentId={selectedTournamentId}
              onTournamentChange={setSelectedTournamentId}
                  onClearSelection={() => setSelectedTournamentId('')}
                  labelText="Select a Tournament:"
                />
              )}
              {viewMode === 'comparison' && (
                <>
                  <h4 className="font-medium whitespace-nowrap">Select tournaments to compare:</h4>
                  <MultiSelectCombobox
                    title="Tournaments"
                    options={tournaments.map(t => ({ label: t.name, value: t.id }))}
                    selectedValues={selectedTournamentsForComparison}
                    onValuesChange={setSelectedTournamentsForComparison}
                    buttonVariant="default"
                  />
                </>
              )}
              <Button
                variant={viewMode === 'overview' ? 'default' : 'outline'}
                onClick={() => setViewMode('overview')}
                className="flex items-center gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                Overview
              </Button>
              <Button
                variant={viewMode === 'comparison' ? 'default' : 'outline'}
                onClick={() => setViewMode('comparison')}
                className="flex items-center gap-2"
              >
                <GitCompare className="h-4 w-4" />
                Comparison
              </Button>
            </div>
          </div>
          {viewMode === 'overview' ? (
          <TournamentOverview
            tournaments={tournaments}
            matches={matches}
            teams={teams}
            selectedTournamentId={selectedTournamentId}
          />
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>

                </CardHeader>
                <CardContent className="space-y-4">
                  <TournamentComparison
                    tournaments={tournaments}
                    matches={matches}
                    teams={teams}
                    selectedTournamentIds={selectedTournamentsForComparison}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* TEAMS TAB */}
        <TabsContent value="teams" className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Team Analytics Dashboard</h2>
            <div className="flex gap-2 items-center">
              <TournamentSelector
                tournaments={tournaments}
                selectedTournamentId={selectedTournamentId}
                onTournamentChange={setSelectedTournamentId}
                onClearSelection={() => setSelectedTournamentId('')}
                labelText="Select a Team:"
              />
              <Button
                variant={viewMode === 'overview' ? 'default' : 'outline'}
                onClick={() => setViewMode('overview')}
                className="flex items-center gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                Overview
              </Button>
              <Button
                variant={viewMode === 'comparison' ? 'default' : 'outline'}
                onClick={() => setViewMode('comparison')}
                className="flex items-center gap-2"
              >
                <GitCompare className="h-4 w-4" />
                Comparison
              </Button>
            </div>
          </div>
          {viewMode === 'overview' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Team Performance Radar</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={stats.teamStats.slice(0, 6).map(team => ({
                      team: team.shortCode || team.name.substring(0, 3),
                      winRate: team.winRate,
                      avgRuns: team.avgRuns / 10,
                      matches: team.matches * 10,
                      players: team.players * 20,
                      consistency: team.consistency
                    }))}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="team" />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} />
                      <Radar name="Win Rate" dataKey="winRate" stroke="#0A5F38" fill="#0A5F38" fillOpacity={0.6} />
                      <Radar name="Consistency" dataKey="consistency" stroke="#3498DB" fill="#3498DB" fillOpacity={0.6} />
                      <Tooltip />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Scoring Phases Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={stats.teamStats.slice(0, 6)} layout="horizontal">
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} />
                      <Tooltip />
                      <Bar dataKey="powerplay" stackId="phases" name="Powerplay" fill="#E74C3C" />
                      <Bar dataKey="middle" stackId="phases" name="Middle Overs" fill="#3498DB" />
                      <Bar dataKey="death" stackId="phases" name="Death Overs" fill="#9B59B6" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Compare Teams</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    <h4 className="font-medium">Select teams to compare:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {teams.map(team => (
                        <div key={team.id} className="flex items-center space-x-2">
                          <Checkbox 
                            id={team.id}
                            checked={selectedTeams.includes(team.id)}
                            onCheckedChange={(checked) => handleMultiSelect('teams', team.id, checked as boolean)}
                          />
                          <label htmlFor={team.id} className="text-sm font-medium">
                            {team.name}
                          </label>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" onClick={() => setSelectedTeams([])}>
                      Clear Selection
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={400}>
                      <RadarChart data={getComparisonData('teams').map(team => ({
                        name: team.shortCode || team.name.substring(0, 3),
                        winRate: team.winRate,
                        avgRuns: team.avgRuns,
                        consistency: team.consistency,
                        players: team.players * 10
                      }))}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="name" />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} />
                        {getComparisonData('teams').map((team, index) => (
                          <Radar
                            key={team.id}
                            name={team.name}
                            dataKey="winRate"
                            stroke={COLORS[index % COLORS.length]}
                            fill={COLORS[index % COLORS.length]}
                            fillOpacity={0.3}
                          />
                        ))}
                        <Tooltip />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>

                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={getComparisonData('teams')}>
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="winRate" name="Win Rate (%)" fill="#0A5F38" />
                        <Bar dataKey="avgRuns" name="Avg Runs" fill="#3498DB" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* PLAYERS TAB */}
        <TabsContent value="players" className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Player Analytics Dashboard</h2>
            <div className="flex gap-2 items-center">
              <TournamentSelector
                tournaments={tournaments}
                selectedTournamentId={selectedTournamentId}
                onTournamentChange={setSelectedTournamentId}
                onClearSelection={() => setSelectedTournamentId('')}
                labelText="Select a Player:"
              />
              <Button
                variant={viewMode === 'overview' ? 'default' : 'outline'}
                onClick={() => setViewMode('overview')}
                className="flex items-center gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                Overview
              </Button>
              <Button
                variant={viewMode === 'comparison' ? 'default' : 'outline'}
                onClick={() => setViewMode('comparison')}
                className="flex items-center gap-2"
              >
                <GitCompare className="h-4 w-4" />
                Comparison
              </Button>
            </div>
          </div>
          {viewMode === 'overview' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Player Role Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={roleDistribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        label={({ role, percentage }) => `${role} (${percentage}%)`}
                      >
                        {roleDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Dismissal Types</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={dismissalTypes}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        label={({ type, percentage }) => `${type} (${percentage}%)`}
                      >
                        {dismissalTypes.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Batting vs Bowling Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <ScatterChart data={stats.playerStats}>
                      <XAxis dataKey="average" name="Batting Average" />
                      <YAxis dataKey="economy" name="Bowling Economy" />
                      <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                      <Scatter name="Players" dataKey="strikeRate" fill="#0A5F38" />
                    </ScatterChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Performance Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={performanceDistribution}>
                      <XAxis dataKey="range" />
                      <YAxis />
                      <Tooltip />
                      <Area type="monotone" dataKey="teams" stackId="1" stroke="#3498DB" fill="#3498DB" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Compare Players</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    <h4 className="font-medium">Select players to compare:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-64 overflow-y-auto">
                      {players.map(player => (
                        <div key={player.id} className="flex items-center space-x-2">
                          <Checkbox 
                            id={player.id}
                            checked={selectedPlayers.includes(player.id)}
                            onCheckedChange={(checked) => handleMultiSelect('players', player.id, checked as boolean)}
                          />
                          <label htmlFor={player.id} className="text-sm font-medium">
                            {player.name} ({player.role})
                          </label>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" onClick={() => setSelectedPlayers([])}>
                      Clear Selection
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={400}>
                      <RadarChart data={getComparisonData('players').map(player => ({
                        name: player.name.split(' ')[0],
                        batting: player.batting,
                        bowling: player.bowling,
                        fielding: player.fielding,
                        fitness: player.fitness,
                        leadership: player.leadership,
                        consistency: player.consistency
                      }))}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="name" />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} />
                        {getComparisonData('players').map((player, index) => (
                          <Radar
                            key={player.id}
                            name={player.name}
                            dataKey="batting"
                            stroke={COLORS[index % COLORS.length]}
                            fill={COLORS[index % COLORS.length]}
                            fillOpacity={0.3}
                          />
                        ))}
                        <Tooltip />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>

                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={getComparisonData('players')}>
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="runs" name="Total Runs" fill="#0A5F38" />
                        <Bar dataKey="wickets" name="Total Wickets" fill="#E74C3C" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* MATCHES TAB */}
        <TabsContent value="matches" className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Match Analytics Dashboard</h2>
            <div className="flex gap-2 items-center">
              <TournamentSelector
                tournaments={tournaments}
                selectedTournamentId={selectedTournamentId}
                onTournamentChange={setSelectedTournamentId}
                onClearSelection={() => setSelectedTournamentId('')}
                labelText="Select a Match:"
              />
              <Button
                variant={viewMode === 'overview' ? 'default' : 'outline'}
                onClick={() => setViewMode('overview')}
                className="flex items-center gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                Overview
              </Button>
              <Button
                variant={viewMode === 'comparison' ? 'default' : 'outline'}
                onClick={() => setViewMode('comparison')}
                className="flex items-center gap-2"
              >
                <GitCompare className="h-4 w-4" />
                Comparison
              </Button>
            </div>
          </div>
          {viewMode === 'overview' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Match Status Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Completed', value: matches.filter(m => m.status === 'completed').length },
                          { name: 'Live', value: matches.filter(m => m.status === 'live').length },
                          { name: 'Upcoming', value: matches.filter(m => m.status === 'upcoming').length }
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label
                      >
                        {[0, 1, 2].map((index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Runs Per Match Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={matches.slice(0, 10).map((match, index) => ({
                      match: `Match ${index + 1}`,
                      totalRuns: match.team1Score.runs + match.team2Score.runs,
                      team1Runs: match.team1Score.runs,
                      team2Runs: match.team2Score.runs
                    }))}>
                      <XAxis dataKey="match" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="totalRuns" name="Total Runs" stroke="#0A5F38" strokeWidth={2} />
                      <Line type="monotone" dataKey="team1Runs" name="Team 1" stroke="#3498DB" strokeWidth={2} />
                      <Line type="monotone" dataKey="team2Runs" name="Team 2" stroke="#E74C3C" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Match Performance Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-500 mb-4">Compare match performance across different tournaments and cities</p>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={matches.slice(0, 8).map((match, index) => {
                    const team1 = teams.find(t => t.id === match.team1Id);
                    const team2 = teams.find(t => t.id === match.team2Id);
                    return {
                      match: `${team1?.shortCode || 'T1'} vs ${team2?.shortCode || 'T2'}`,
                      team1Runs: match.team1Score.runs,
                      team2Runs: match.team2Score.runs,
                      totalRuns: match.team1Score.runs + match.team2Score.runs
                    };
                  })}>
                    <XAxis dataKey="match" angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="team1Runs" name="Team 1 Runs" fill="#0A5F38" />
                    <Bar dataKey="team2Runs" name="Team 2 Runs" fill="#3498DB" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StatisticsPage;
